<template>
  <v-data-table
    :loading="loading"
    :headers="aggregationTable.headers"
    :items="aggregationTable.items"
    :header-props="{ sortByText: 'ソート' }"
    :footer-props="{ itemsPerPageText: '行/ページ:' }"
    no-data-text="データがありません"
    loading-text="読込中..."
    :hide-default-footer="aggregationTable.items.length === 1"
    class="aggregation-table"
    :class="{ 'fixed-first-column': isUserAggregation }"
  >
    <template #top>
      <v-toolbar
        flat
        color="transparent"
      >
        <v-toolbar-title>{{ aggregationTable.name }}</v-toolbar-title>
        <v-spacer />

        <v-btn
          icon
          depressed
          :ripple="false"
          :loading="loading"
          :disabled="loading"
          @click.prevent="$emit('reload', aggregationTable.id)"
        >
          <v-icon>
            {{ icons.mdiReload }}
          </v-icon>
        </v-btn>
        <v-divider
          vertical
          inset
          class="mx-2"
        />
        <v-menu
          bottom
          left
        >
          <template #activator="{ on: menuOn, attrs: menuAttrs }">
            <v-btn
              icon
              v-bind="menuAttrs"
              v-on="menuOn"
            >
              <v-icon>{{ icons.mdiDotsVertical }}</v-icon>
            </v-btn>
          </template>

          <template #default="menu">
            <v-list
              :disabled="loading"
              dense
            >
              <v-subheader>操作</v-subheader>
              <AggregationUsersDialogListItem
                v-if="isUserAggregation"
                :disabled="loading"
                :aggregation-users="aggregationUsers"
                :available-users="availableUsers"
                @create="$emit('create-user-aggregation', $event)"
                @fetch-available-users="$emit('fetch-available-users', $event)"
                @destroy="$emit('destroy-user-aggregation', $event)"
                @cancel="menu.value = false"
              />
              <DataTableCopyListItemBtn
                :disabled="loading"
                :headers="aggregationTable.headers"
                :items="aggregationTable.items"
              />
            </v-list>
          </template>
        </v-menu>
      </v-toolbar>

      <!-- セル編集用の共有メニュー(flyweight)。セルごとに v-menu を持たず、クリックされたセルの位置に1個だけ表示する -->
      <v-menu
        v-model="editMenu.open"
        :position-x="editMenu.x"
        :position-y="editMenu.y"
        absolute
        :close-on-content-click="false"
        :nudge-width="200"
      >
        <v-card v-if="editMenu.item">
          <v-card-subtitle>{{ editMenu.headerText }}</v-card-subtitle>
          <v-card-text>
            <v-checkbox
              v-if="editMenu.inputType === 'boolean'"
              v-model="editingValue"
              autofocus
              dense
              hide-details
            />
            <v-text-field
              v-else-if="editMenu.inputType === 'number'"
              v-model="editingValue"
              v-format-number-on-blur
              autofocus
              type="number"
              inputmode="numeric"
              pattern="[0-9]*"
              dense
              hide-details
            />
            <v-text-field
              v-else
              v-model="editingValue"
              autofocus
              :type="editMenu.inputType"
              dense
              hide-details
            />
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn
              text
              small
              @click="editMenu.open = false"
            >
              キャンセル
            </v-btn>
            <v-btn
              color="primary"
              small
              :disabled="loading"
              @click="saveEditingValue"
            >
              保存
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-menu>
    </template>

    <!-- eslint-disable-next-line vue/no-useless-template-attributes -->
    <template
      v-for="header in aggregationTable.businessRuleHeaders"
      #[`item.${header.value}`]="{ item }"
    >
      <div
        :key="`business-rule-result-cell-for-current-club-business-rule-id-${header.value}`"
        class="aggregation-cell"
        :class="{
          'aggregation-cell--editable': !isNotEditable(item, header.value),
          'aggregation-cell--new': !isNotEditable(item, header.value) && isNew(item, header.value),
        }"
        @click="openEditMenu($event, item, header)"
      >
        <v-icon
          v-if="isBooleanItem(item, header.value)"
          small
          :color="item[header.value] ? 'primary' : ''"
        >
          {{ item[header.value] ? icons.mdiCheckboxMarked : icons.mdiCheckboxBlankOutline }}
        </v-icon>
        <template v-else-if="isNumberItem(item, header.value)">
          {{ numberText(item, header.value) }}
        </template>
        <template v-else>
          {{ item[header.value] }}
        </template>
      </div>
    </template>
  </v-data-table>
</template>

<script>
import { reactive, ref, nextTick } from '@vue/composition-api'
import {
  mdiReload,
  mdiDotsVertical,
  mdiCheckboxMarked,
  mdiCheckboxBlankOutline,
} from '@mdi/js'
import formatNumberOnBlur from '@/directives/formatNumberOnBlur'
import useAggregationTable from '@/views/v2-temp/eod-closing/composables/useAggregationTable'
import AggregationUsersDialogListItem from '@/views/v2-temp/eod-closing/components/AggregationUsersDialogListItem.vue'
import DataTableCopyListItemBtn from '@/views/v2-temp/common/components/DataTableCopyListItemBtn.vue'

export default {
  components: {
    AggregationUsersDialogListItem,
    DataTableCopyListItemBtn,
  },
  directives: {
    formatNumberOnBlur,
  },
  props: {
    aggregation: {
      type: Object,
      default: () => {
        return {
          attributes: {
            id: null,
            name: '',
            description: '',
            businessRules: { data: [] },
            results: { data: [] },
            contexts: { data: [{ attributes: { results: { data: [] } } }] },
          },
        }
      },
    },
    aggregationUsers: {
      type: Array,
      default: () => [],
    },
    availableUsers: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const {
      aggregationTable,
      isUserAggregation,
      isBooleanItem,
      isNumberItem,
      isNew,
      isNotEditable,
      inputType,
      numberText,
      saveResultValue,
    } = useAggregationTable(props, emit)

    // セル編集用の共有メニュー状態(flyweight)。編集UIは表示中の1セル分だけ生成する
    const editMenu = reactive({
      open: false,
      x: 0,
      y: 0,
      item: null,
      businessRuleId: null,
      headerText: '',
      inputType: 'text',
    })
    const editingValue = ref(null)

    const openEditMenu = (event, item, header) => {
      if (isNotEditable.value(item, header.value)) return

      const rect = event.currentTarget.getBoundingClientRect()
      editMenu.open = false
      editMenu.item = item
      editMenu.businessRuleId = header.value
      editMenu.headerText = header.text
      editMenu.inputType = inputType.value(item, header.value)
      editingValue.value = item[header.value]
      editMenu.x = rect.left
      editMenu.y = rect.bottom
      nextTick(() => {
        editMenu.open = true
      })
    }

    const saveEditingValue = () => {
      const { item, businessRuleId } = editMenu
      if (!item) return

      item[businessRuleId] = editingValue.value
      saveResultValue(item, businessRuleId, editingValue.value)
      editMenu.open = false
    }

    return {
      isBooleanItem,
      isNumberItem,
      numberText,
      isNotEditable,
      isUserAggregation,
      isNew,
      aggregationTable,
      editMenu,
      editingValue,
      openEditMenu,
      saveEditingValue,
      icons: {
        mdiReload,
        mdiDotsVertical,
        mdiCheckboxMarked,
        mdiCheckboxBlankOutline,
      },
    }
  },
}
</script>

<style lang="scss">
@import '~@core/preset/preset/mixins.scss';

.aggregation-table {
  white-space: nowrap;

  .aggregation-cell {
    display: flex;
    align-items: center;
    min-height: 32px;
  }

  .aggregation-cell--editable {
    cursor: pointer;
  }

  /* 新規作成(未保存)の値に適用するスタイル */
  .aggregation-cell--new {
    border-bottom: 2px solid #9155FD;
  }
}
</style>
